import type React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { HtmlEditorMode } from './editor-types';
import { FormattingToolbar } from './formatting-toolbar.component';
import { ModeToggleButton } from './mode-toggle.component';
import { HtmlSourceEditor } from './source-editor.component';
import { HtmlVisualEditor } from './visual-editor.component';

export interface HtmlBodyEditorProps {
    value: string;
    onChange(_value: string): void;
    mode: HtmlEditorMode;
    onModeChange(_mode: HtmlEditorMode): void;
    onFormatSource?(): void;
    toolbarEnd?: React.ReactNode;
    editorKey?: string;
    head?: string;
    onHeadChange?(_head: string): void;
    headTitle?: React.ReactNode;
    bodySourceTitle?: React.ReactNode;
    iframeTitle?: string;
    iframeStyle?: React.CSSProperties;
    enableEmailRegions?: boolean;
}

export const HtmlBodyEditor = (props: HtmlBodyEditorProps) => {
    const intl = useIntl();
    const visualLabel = intl.formatMessage({
        id: 'content-detail.editor.visual-mode',
        description: 'Button label for switching notification content editor to visual editing mode',
        defaultMessage: 'Visual'
    });
    const sourceLabel = intl.formatMessage({
        id: 'content-detail.editor.source-mode',
        description: 'Button label for switching notification content editor to raw HTML source mode',
        defaultMessage: 'HTML'
    });

    return (
        <>
            <div className='outline-border-default inline-flex items-center justify-between self-stretch px-3 py-2 outline-1 outline-offset-[-1px]'>
                <div className='inline-flex items-center gap-1'>
                    <ModeToggleButton
                        active={props.mode === 'visual'}
                        label={visualLabel}
                        onClick={() => props.onModeChange('visual')}>
                        {visualLabel}
                    </ModeToggleButton>
                    <ModeToggleButton
                        active={props.mode === 'source'}
                        label={sourceLabel}
                        onClick={() => props.onModeChange('source')}>
                        {sourceLabel}
                    </ModeToggleButton>
                </div>
                {props.toolbarEnd}
            </div>
            <FormattingToolbar
                mode={props.mode}
                enableEmailRegions={props.enableEmailRegions}
                sourceActions={
                    props.onFormatSource ? (
                        <button
                            type='button'
                            className='text-brand-primary outline-border-default hover:outline-brand-primary flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium outline-1 outline-offset-[-1px] hover:cursor-pointer'
                            onClick={props.onFormatSource}>
                            <FormattedMessage
                                id='content-detail.editor.format-html'
                                description='Button label for formatting notification content HTML source'
                                defaultMessage='Format HTML'
                            />
                        </button>
                    ) : undefined
                }
            />
            {props.mode === 'visual' ? (
                <HtmlVisualEditor
                    key={`${props.editorKey ?? 'html-visual'}-visual`}
                    value={props.value}
                    onChange={props.onChange}
                    head={props.head}
                    iframeTitle={props.iframeTitle}
                    iframeStyle={props.iframeStyle}
                />
            ) : props.onHeadChange ? (
                <div className='mb-3 flex flex-col gap-3 self-stretch'>
                    <HtmlSourceEditor
                        title={props.headTitle}
                        value={props.head ?? ''}
                        onChange={props.onHeadChange}
                        minHeightClassName='min-h-48'
                    />
                    <HtmlSourceEditor
                        title={props.bodySourceTitle}
                        value={props.value}
                        onChange={props.onChange}
                    />
                </div>
            ) : (
                <HtmlSourceEditor
                    value={props.value}
                    onChange={props.onChange}
                    title={props.bodySourceTitle}
                />
            )}
        </>
    );
};
